import type { Canvas } from 'fabric';
import { canvasService } from '@/services/canvasService';
import { skuService } from '@/services/skuService';
import { extractJsonDataFromFile } from '@/utils/excel';
import { getLayerObjects, getLayerType, type LayerObject } from './CanvasLayers';

export interface BatchFormatColumn {
  header: string;
  kind: 'sku' | 'text';
  value: string;
  // Ids and names of this capa. A filled cell that matches one is a SKU already on the canvas.
  acceptedValues: string[];
}

export interface BatchValidationIssue {
  row: number;
  message: string;
}

export interface BatchValidationProgress {
  processed: number;
  total: number;
}

export interface BatchValidationResult {
  unreadable: boolean;
  issues: BatchValidationIssue[];
}

const escapeCsvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

const skuCellValue = (object: LayerObject): string => {
  const name = object.layerName?.trim();
  if (name) return name;
  return object.sourceRef?.id === undefined ? '' : String(object.sourceRef.id);
};

const textCellValue = (object: LayerObject): string => String((object as LayerObject & { text?: string }).text ?? '');

const acceptedSkuValues = (object: LayerObject, value: string): string[] => {
  const id = object.sourceRef?.id;
  return [...new Set([value, id === undefined ? '' : String(id)].map((item) => item.trim()).filter(Boolean))];
};

// One column per SKU capa and per text capa: SKUs first, then texts, each back to front.
export const getBatchFormatColumns = (canvas: Canvas | null): BatchFormatColumn[] => {
  const layers = canvas ? getLayerObjects(canvas) : [];
  const skus = layers.filter((object) => getLayerType(object) !== 'text' && object.sourceRef?.kind === 'sku');
  const texts = layers.filter((object) => getLayerType(object) === 'text');

  return [
    ...skus.map((object, index) => {
      const value = skuCellValue(object);
      return {
        header: index === 0 ? 'sku' : `sku_${index + 1}`,
        kind: 'sku' as const,
        value,
        acceptedValues: acceptedSkuValues(object, value),
      };
    }),
    ...texts.map((object, index) => ({
      header: index === 0 ? 'text' : `text_${index + 1}`,
      kind: 'text' as const,
      value: textCellValue(object),
      acceptedValues: [],
    })),
  ];
};

// Batch template. The row under the headers is what's on the canvas now. A canvas with neither still
// downloads `sku,text`. BOM so Excel opens the accents correctly.
export const buildBatchFormatCsv = (canvas: Canvas | null): string => {
  const columns = getBatchFormatColumns(canvas);
  const headers = columns.length > 0 ? columns.map((column) => column.header) : ['sku', 'text'];
  const values = columns.map((column) => column.value);
  const rows = values.length > 0 ? [headers, values] : [headers];
  return `\uFEFF${rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n')}\n`;
};

const parseDelimited = (text: string, delimiter: string): string[][] => {
  const source = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  const pushRow = () => {
    row.push(cell);
    rows.push(row);
    row = [];
    cell = '';
  };

  for (let index = 0; index < source.length; index++) {
    const char = source[index];
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
      continue;
    }
    if (char === delimiter) {
      row.push(cell);
      cell = '';
      continue;
    }
    if (char === '\n') {
      pushRow();
      continue;
    }
    cell += char;
  }
  if (cell.length > 0 || row.length > 0) pushRow();
  return rows;
};

const parseCsv = (text: string): string[][] => {
  const commaRows = parseDelimited(text, ',');
  const header = commaRows[0]?.[0] ?? '';
  if (commaRows[0]?.length === 1 && header.includes(';')) return parseDelimited(text, ';');
  return commaRows;
};

const readFileText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.readAsText(file);
  });

const readTable = async (file: File): Promise<string[][] | null> => {
  try {
    if (/\.xlsx$/i.test(file.name)) {
      const { rows } = await extractJsonDataFromFile(file);
      return rows;
    }
    if (!/\.csv$/i.test(file.name)) return null;
    return parseCsv(await readFileText(file));
  } catch {
    return null;
  }
};

const sameValue = (left: string, right: string) => left.trim().toLowerCase() === right.trim().toLowerCase();

const rowMatchesSku = (id: unknown, data: Record<string, unknown> | undefined, value: string) =>
  [id, data?.name, data?.nombre, data?.sku_number].some((candidate) => sameValue(String(candidate ?? ''), value));

// A SKU cell is valid when the library has that id, name, or SKU number.
export const libraryHasSku = async (value: string): Promise<boolean> => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  const list = await skuService.getSKUList({ search: trimmed, page: 1, page_size: 50 });
  const foundInList = list.results.some((row) =>
    rowMatchesSku(row.id, row.data as unknown as Record<string, unknown>, trimmed)
  );
  if (foundInList) return true;

  const byId = await skuService.getSKU(trimmed);
  return Boolean(byId.success && byId.data);
};

const nextTick = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

const expectedColumns = (canvas: Canvas | null): BatchFormatColumn[] => {
  const columns = getBatchFormatColumns(canvas);
  if (columns.length > 0) return columns;
  return [
    { header: 'sku', kind: 'sku', value: '', acceptedValues: [] },
    { header: 'text', kind: 'text', value: '', acceptedValues: [] },
  ];
};

// Checks the filled file against the capas on the canvas. SKU cells must be a capa already on the
// canvas or a SKU in the library. `onProgress` reports data rows as they are checked.
export const validateBatchFile = async (
  file: File,
  canvas: Canvas | null,
  onProgress?: (progress: BatchValidationProgress) => void,
  hasSku: (value: string) => Promise<boolean> = libraryHasSku
): Promise<BatchValidationResult> => {
  const table = await readTable(file);
  const header = table?.[0]?.map((cell) => cell.trim()) ?? [];
  if (!table || header.every((cell) => cell === '')) return { unreadable: true, issues: [] };

  const columns = expectedColumns(canvas);
  const issues: BatchValidationIssue[] = [];
  columns.forEach((column) => {
    if (!header.includes(column.header)) issues.push({ row: 1, message: `Columna "${column.header}" faltante` });
  });
  header.forEach((cell) => {
    if (cell && !columns.some((column) => column.header === cell)) {
      issues.push({ row: 1, message: `Columna "${cell}" con formato inválido` });
    }
  });

  const knownSkus = new Set(columns.flatMap((column) => column.acceptedValues.map((value) => value.trim().toLowerCase())));
  const checked = new Map<string, boolean>();
  const skuIsValid = async (value: string) => {
    const key = value.trim().toLowerCase();
    if (knownSkus.has(key)) return true;
    const cached = checked.get(key);
    if (cached !== undefined) return cached;
    const found = await hasSku(value.trim());
    checked.set(key, found);
    return found;
  };

  const records = table.slice(1).map((cells, index) => ({ row: index + 2, cells }));
  const filled = records.filter((record) => record.cells.some((cell) => cell.trim() !== ''));
  if (filled.length === 0) issues.push({ row: 1, message: 'El archivo no contiene registros' });

  onProgress?.({ processed: 0, total: filled.length });
  for (const [index, record] of filled.entries()) {
    for (const column of columns) {
      if (!header.includes(column.header)) continue;
      const value = (record.cells[header.indexOf(column.header)] ?? '').trim();
      if (!value) {
        issues.push({ row: record.row, message: `Variable {{${column.header}}} faltante` });
        continue;
      }
      if (column.kind === 'sku' && !(await skuIsValid(value))) {
        issues.push({ row: record.row, message: 'SKU ID no encontrado en la librería' });
      }
    }
    onProgress?.({ processed: index + 1, total: filled.length });
    await nextTick();
  }

  return { unreadable: false, issues };
};

export const CANVAS_BATCH_POLL_MS = 1500;

export interface BatchRecord {
  skus: string[];
  texts: string[];
}

// One record per filled row, with the SKU and text cells in the same order as the canvas capas.
export const readBatchRecords = async (file: File, canvas: Canvas | null): Promise<BatchRecord[]> => {
  const table = await readTable(file);
  const header = table?.[0]?.map((cell) => cell.trim()) ?? [];
  if (!table || header.every((cell) => cell === '')) return [];

  const columns = getBatchFormatColumns(canvas);
  return table
    .slice(1)
    .filter((cells) => cells.some((cell) => cell.trim() !== ''))
    .map((cells) => {
      const skus: string[] = [];
      const texts: string[] = [];
      columns.forEach((column) => {
        const value = (cells[header.indexOf(column.header)] ?? '').trim();
        (column.kind === 'sku' ? skus : texts).push(value);
      });
      return { skus, texts };
    });
};

// Image of a library SKU named by the cell (id, name, or SKU number). Null when the library has none.
export const findSkuImageUrl = async (value: string): Promise<string | null> => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const list = await skuService.getSKUList({ search: trimmed, page: 1, page_size: 50 });
  const match = list.results.find((row) => rowMatchesSku(row.id, row.data as unknown as Record<string, unknown>, trimmed));
  const listed = match?.data?.imagen?.trim();
  if (listed) return listed;

  const byId = await skuService.getSKU(trimmed);
  const image = byId.success ? byId.data?.image?.trim() : '';
  return image || null;
};

export interface CanvasBatchOutcome {
  taskId: string;
  count: number;
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Uploads the valid file and waits on the same Celery job the other exports use.
export const runCanvasBatch = async (
  canvasId: string,
  file: File,
  onProgress?: (progress: BatchValidationProgress) => void,
  pollMs = CANVAS_BATCH_POLL_MS,
  images: Blob[] = [],
): Promise<CanvasBatchOutcome> => {
  const { taskId } = await canvasService.startCanvasBatch(canvasId, file, images);
  for (;;) {
    const status = await canvasService.getCanvasBatchStatus(taskId);
    onProgress?.({ processed: status.processed, total: status.total });
    if (status.status === 'success') return { taskId, count: status.combosCount };
    if (status.status !== 'pending' && status.status !== 'started') {
      throw new Error(status.error || 'No se pudieron generar los combos');
    }
    await pause(pollMs);
  }
};
